import { Injectable, inject } from '@angular/core';
import { SettingsService } from './settings.service';
import { ContentType, getUniqueName, makeid } from 'na-lib';
import { prepareDataToFirestoreUpdates, removeUndefinedValuesFromObject } from 'na-lib';
import { NetworkConstants } from 'na-lib';
import { PassDefinitionsService } from './pass-definitions.service';
import { doc, updateDoc, setDoc, collection, deleteDoc, deleteField, Firestore } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root'
})
export class ContentTypeService {
  db = inject(Firestore)
  contentTypes_schema:any={}
  accountLanguages:any=[]

  constructor(
    private settingsService: SettingsService,
    private passService: PassDefinitionsService) { }

  async updateContentTypeTimestamp(contentTypeId: string): Promise<void> {
    try {
      const timestamp = new Date();
      const docRef = doc(this.db, NetworkConstants.COLLECTION_CONTEXTS, this.settingsService.contextId$,NetworkConstants.COLLECTION_CONTENT_TYPES, contentTypeId )
      await updateDoc( docRef, { 'timeStamp': timestamp })
    } catch (error) {
      console.error('Error updating timestamp:', error);
      throw error;
    }
  }

  updateContentType(contentType: ContentType, oldContentTypeData: ContentType): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const cleanedData = removeUndefinedValuesFromObject(contentType);
        const newdata = prepareDataToFirestoreUpdates(cleanedData);

        if (contentType.schema !== undefined) newdata['schema'] = contentType.schema;

        // ContentType comes from na-lib and does not model application-specific extensions.
        // Keep the schema metadata template when callers attach it to the instance.
        const metaTemplate = (contentType as ContentType & { metaTemplate?: unknown }).metaTemplate;
        if (metaTemplate !== undefined) newdata['metaTemplate'] = metaTemplate;

        if (!contentType.titleKey || contentType.titleKey.trim() === "")
          newdata['titleKey'] = deleteField()
        
        newdata['timeStamp'] = new Date();
        const docRef = doc(this.db, NetworkConstants.COLLECTION_CONTEXTS, this.settingsService.contextId$,NetworkConstants.COLLECTION_CONTENT_TYPES, contentType.id )
        await updateDoc( docRef, newdata)

        if (contentType.displayName !== oldContentTypeData.displayName && contentType.passTemplateId)
          this.passService.updatePassDefinitionAfterUpdateContentType(contentType.passTemplateId, contentType.displayName);

        resolve();
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  }

  deleteContentType(contentTypeId:any):Promise<void>{
    return new Promise(async (resolve, reject) => {
      try {
        const docRef = doc(this.db, NetworkConstants.COLLECTION_CONTEXTS, this.settingsService.contextId$,NetworkConstants.COLLECTION_CONTENT_TYPES, contentTypeId)
        const d  = await deleteDoc( docRef)
        resolve(d)
      } catch (error) {
        reject(error)
        console.log(error)
      }
    })
  }

  async createContentType(newContent:any):Promise<any>{
    return new Promise(async (resolve, reject) => {
      try{
        const docRef = doc( collection(this.db,NetworkConstants.COLLECTION_CONTEXTS, this.settingsService.contextId$, NetworkConstants.COLLECTION_CONTENT_TYPES) )
        const newId = docRef.id
        const passid = await this.passService.createPassDefinition(newContent.displayName, newId, newContent.language)

        newContent['id']= newId
        newContent['creationDate'] = new Date()
        newContent['timeStamp'] = new Date()
        newContent['passTemplateId'] = passid
        newContent['state']="active"
        const nfcURLPrefix = makeid(6)
        newContent.nfcURLPrefix = nfcURLPrefix

        await setDoc( docRef, newContent)

        const data = { contentTypeId: newId, passTemplateId: passid }
        resolve(data)
      }catch(error){
        console.log(error)
      }
    })

  }

  /** Creates a schema-first content type without creating a pass template. */
  async createStandaloneContentType(newContent: any): Promise<{ contentTypeId: string }> {
    const docRef = doc(collection(this.db, NetworkConstants.COLLECTION_CONTEXTS, this.settingsService.contextId$, NetworkConstants.COLLECTION_CONTENT_TYPES));
    const contentType = {
      ...newContent,
      id: docRef.id,
      creationDate: new Date(),
      timeStamp: new Date(),
      state: 'active',
      nfcURLPrefix: makeid(6),
      schema: { type: 'object', properties: [] },
      schemaMode: 'warning'
    };
    await setDoc(docRef, contentType);
    return { contentTypeId: docRef.id };
  }

  /** Links the single pass template to a previously created content type. */
  async createPassTemplateForContentType(contentType: any): Promise<{ passTemplateId: string }> {
    if (contentType?.passTemplateId) return { passTemplateId: contentType.passTemplateId };
    const languages = Array.isArray(contentType?.language)
      ? contentType.language
      : contentType?.language ? [contentType.language] : this.settingsService.languagesContexts$;
    const passTemplateId = await this.passService.createPassDefinition(contentType.displayName, contentType.id, languages);
    await Promise.all([
      updateDoc(doc(this.db, NetworkConstants.COLLECTION_CONTEXTS, this.settingsService.contextId$, NetworkConstants.COLLECTION_CONTENT_TYPES, contentType.id), {
        passTemplateId,
        timeStamp: new Date()
      }),
      updateDoc(doc(this.db, NetworkConstants.COLLECTION_CONTEXTS, this.settingsService.contextId$, NetworkConstants.COLLECTION_PASSDEFINITIONS, passTemplateId), {
        contentType: contentType.id,
        timeStamp: new Date()
      })
    ]);
    return { passTemplateId };
  }

  setSchemaOfContentTypeID(contentTypeId,schema){
    this.contentTypes_schema[contentTypeId]=schema
  }

  resetSchemaOfContentTypeID(contentTypeId){
    if(this.contentTypes_schema[contentTypeId])
    delete this.contentTypes_schema[contentTypeId]
  }

  getSchemaOfContentTypeID(contentTypeId){
    return this.contentTypes_schema[contentTypeId]
  }


  async importContentype(newContent:any,  newPass):Promise<any>{
    return new Promise(async (resolve, reject) => {
      try{

        const docRef = doc( collection(this.db,NetworkConstants.COLLECTION_CONTEXTS, this.settingsService.contextId$, NetworkConstants.COLLECTION_CONTENT_TYPES) )
        const newId = docRef.id
        const passid = await this.passService.importPassDefinition(newPass, newId)

        newContent['id']= newId
        newContent['creationDate'] = new Date()
        newContent['timeStamp'] = new Date()
        newContent['passTemplateId'] = passid
        newContent['state']="active"

        const existingContentTypes = this.settingsService.settingsContentTypesWithId ?? []
        const existingDisplayNames = existingContentTypes.map(ct => ct?.displayName)
        const importedDisplayName = `${newContent['displayName'] ?? ''} import_`
        
        newContent['displayName'] = getUniqueName(importedDisplayName, existingDisplayNames)

        await setDoc( docRef, newContent)

        const data = { contentTypeId: newId, passTemplateId: passid }
        resolve(data)
      }catch(error){
        console.log(error)
      }
    })

  }

}
